import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { featureFlags, type FeatureFlag, type FlagCreate } from "@/api/platformAdmin";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Feature flags — org-wide toggles + gradual rollout %. A user_id-scoped row
 * (shown as an override) wins over the org default; this page manages org flags.
 */
export function AdminFeatureFlagsPage() {
  const list = useQuery({ queryKey: ["feature-flags"], queryFn: () => featureFlags.list() });
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (f: FeatureFlag) => featureFlags.patch(f.id, { value: !f.value }),
    onSuccess: () => {
      toast.push("Flag updated", "success");
      qc.invalidateQueries({ queryKey: ["feature-flags"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => featureFlags.remove(id),
    onSuccess: () => {
      toast.push("Flag deleted", "warn");
      qc.invalidateQueries({ queryKey: ["feature-flags"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Feature flags</h1>
        <Button onClick={() => setOpen(true)}>New flag</Button>
      </div>
      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="No flags" description="Create org-wide feature toggles with optional rollout %." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Key</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Scope</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Rollout %</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Value</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((f) => (
                  <tr key={f.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{f.flag_key}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      {f.user_id ? `user ${f.user_id.slice(0, 8)}` : "org"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{f.rollout_pct}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={f.value ? "good" : "neutral"}>{f.value ? "ON" : "OFF"}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => toggle.mutate(f)} disabled={toggle.isPending}>
                          {f.value ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Delete flag "${f.flag_key}"?`)) remove.mutate(f.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {open && <FlagModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function FlagModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<FlagCreate>({ flag_key: "", value: false, rollout_pct: 0 });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => featureFlags.create(form),
    onSuccess: () => {
      toast.push("Flag created", "success");
      qc.invalidateQueries({ queryKey: ["feature-flags"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="New feature flag">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Flag key</Label>
          <Input
            value={form.flag_key}
            onChange={(e) => setForm({ ...form, flag_key: e.target.value })}
            placeholder="new_terminal_layout"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.checked })}
            />
            Enabled
          </label>
          <div>
            <Label>Rollout %</Label>
            <Input
              value={String(form.rollout_pct ?? 0)}
              onChange={(e) => setForm({ ...form, rollout_pct: parseInt(e.target.value || "0", 10) })}
            />
          </div>
        </div>
        <div>
          <Label>Note</Label>
          <Input
            value={form.note ?? ""}
            onChange={(e) => setForm({ ...form, note: e.target.value || null })}
            placeholder="optional"
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
